// Shared write path for PlayerGuess commands.

import { useCallback } from 'react';
import type { ContentType, GameElementType } from '@airhead-games/shared/types';
import {
  generateCommandId,
  getGameElementCollectionKey,
  isValidElementId,
  parseElementId,
} from '@airhead-games/shared/utilities';
import { useRoomConfig } from './useRoomConfig';
import { useRoomGameElements } from './useRoomGameElements';
import { useRoomMyPlayer } from './useRoomMyPlayer';
import { useRoomSocket } from './useRoomSocket';

interface PlayerGuessOptions {
  contentGroup?: ContentType;
  parentElementIdOverride?: string;
}

export function usePlayerGuess() {
  const { roomCode } = useRoomConfig();
  const { getElementById } = useRoomGameElements();
  const myPlayer = useRoomMyPlayer();
  const { sendCommand } = useRoomSocket();

  return useCallback(
    (
      targetElementId: string,
      content: ContentType,
      options: PlayerGuessOptions = {}
    ) => {
      const parsedTargetId = parseElementId(targetElementId);

      // Keep guess submission quiet for incomplete room/player state,
      // malformed IDs, or IDs outside the game-element collections.
      if (!parsedTargetId || !myPlayer || !roomCode) return;

      if (!getGameElementCollectionKey(parsedTargetId.elementType)) return;
      const elementType = parsedTargetId.elementType as GameElementType;

      const targetElement = getElementById(targetElementId);

      // Most guesses keep their existing parent. Allow an explicit valid
      // override for any future game that re-targets a guess.
      const parentElementId =
        options.parentElementIdOverride &&
        isValidElementId(options.parentElementIdOverride)
          ? options.parentElementIdOverride
          : targetElement?.parentId;

      sendCommand({
        id: generateCommandId(),
        type: 'PlayerGuess',
        elementType,
        playerId: myPlayer.playerId,
        content,
        contentGroup: options.contentGroup,
        elementId: targetElementId,
        parentElementId,
        roomCode,
        senderRole: myPlayer.role,
        clientTimestamp: Date.now(),
      });
    },
    [getElementById, myPlayer, roomCode, sendCommand]
  );
}
